import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useMessage } from '../../contexts/MessageContext';

const apiUrl = window._env_?.REACT_APP_API_URL || 'https://app.mylocaleventrentals.com/api';

const ALLOWED_HINT =
  'Allowed tags: p, br, strong / b, em / i, u, h3, h4, ul, ol, li, a (https links). Everything else is stripped on save.';

const ContractTermsTab = () => {
  const { showSuccess, showError } = useMessage();
  const [termsHtml, setTermsHtml] = useState('');
  const [isDefault, setIsDefault] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${apiUrl}/settings/contract-terms`, {
          headers: { 'x-auth-token': token },
        });
        setTermsHtml(res.data?.body_html || '');
        setIsDefault(!!res.data?.is_default);
      } catch (err) {
        showError(err.response?.data?.message || 'Failed to load contract terms');
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSave = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      showError('Please sign in to save settings.');
      return;
    }
    setSaving(true);
    try {
      const res = await axios.patch(
        `${apiUrl}/settings/contract-terms`,
        { body_html: termsHtml },
        { headers: { 'x-auth-token': token } }
      );
      setTermsHtml(res.data?.data?.body_html || termsHtml);
      setIsDefault(!!res.data?.data?.is_default);
      showSuccess('Contract terms saved.');
    } catch (err) {
      showError(err.response?.data?.message || 'Failed to save contract terms');
    } finally {
      setSaving(false);
    }
  };

  const handleRestoreDefault = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      showError('Please sign in to save settings.');
      return;
    }
    setSaving(true);
    try {
      const res = await axios.patch(
        `${apiUrl}/settings/contract-terms`,
        { body_html: null }, // clear -> server returns default
        { headers: { 'x-auth-token': token } }
      );
      setTermsHtml(res.data?.data?.body_html || '');
      setIsDefault(true);
      showSuccess('Contract terms reset to default.');
    } catch (err) {
      showError(err.response?.data?.message || 'Failed to restore default terms');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="text-gray-500 text-sm p-4">Loading contract terms…</div>;
  }

  return (
    <div className="space-y-4 max-w-4xl">
      <div>
        <h2 className="text-lg font-semibold text-gray-900">Contract terms &amp; conditions</h2>
        <p className="text-sm text-gray-500 mt-1">
          This text appears in the <strong>Terms</strong> section of every new rental contract (HTML page + PDF).
          Applies to newly created contracts only — signed contracts keep the version they were signed with.
        </p>
      </div>

      {isDefault && (
        <div className="rounded-md bg-amber-50 border border-amber-200 text-amber-800 text-sm px-4 py-2">
          No custom terms saved — contracts currently use the built-in default text shown below.
        </div>
      )}

      <textarea
        className="w-full border border-gray-300 rounded-md p-3 text-sm font-mono min-h-[260px] focus:outline-none focus:ring-2 focus:ring-blue-500"
        value={termsHtml}
        onChange={(e) => setTermsHtml(e.target.value)}
        placeholder={'<p>1. The customer agrees to ...</p>\n<ul>\n  <li>...</li>\n</ul>'}
        spellCheck={false}
      />
      <div className="flex items-center justify-between text-xs text-gray-400">
        <span>{ALLOWED_HINT}</span>
        <span>{termsHtml.length.toLocaleString()} chars</span>
      </div>

      <div className="flex gap-2">
        <button
          onClick={handleSave}
          disabled={saving}
          className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-50"
        >
          {saving ? 'Saving…' : 'Save terms'}
        </button>
        <button
          onClick={() => setShowPreview((v) => !v)}
          className="px-4 py-2 bg-gray-100 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-200"
        >
          {showPreview ? 'Hide preview' : 'Preview'}
        </button>
        {!isDefault && (
          <button
            onClick={handleRestoreDefault}
            disabled={saving}
            className="px-4 py-2 bg-white border border-red-300 text-red-600 text-sm font-medium rounded-md hover:bg-red-50 disabled:opacity-50"
          >
            Restore default
          </button>
        )}
      </div>

      {showPreview && (
        <div className="border border-gray-200 rounded-md p-5 bg-white shadow-sm">
          <p className="text-xs text-gray-400 mb-3 uppercase tracking-wide">Preview (as it will appear in contracts)</p>
          {/* Sanitized server-side on save; preview shows what staff will have after save. */}
          <div
            className="contract-terms-preview text-[13px] leading-relaxed text-gray-800 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_h3]:font-semibold [&_h4]:font-semibold [&_a]:text-blue-600 [&_a]:underline"
            dangerouslySetInnerHTML={{ __html: termsHtml }}
          />
        </div>
      )}
    </div>
  );
};

export default ContractTermsTab;
